'use client'

import { usePathname } from 'next/navigation'
import {
  type JSX,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { type Dictionary, getDictionary } from '@src/i18n/dictionary'
import {
  getLocaleCodeFromCountryLang,
  getLocaleFromCode,
  getLocaleFromPath,
} from '@src/i18n/locale.util'
import type { CountryCodeLowercase, Lang, LocaleConfig } from '@src/i18n/locales'

interface LocaleContextType {
  dictionary: Dictionary
  locale: LocaleConfig
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

interface LocaleProviderProps {
  children: ReactNode
  lang: Lang
  country: CountryCodeLowercase
}

export const LocaleProvider = (props: LocaleProviderProps) => {
  const [country, setCountry] = useState<CountryCodeLowercase>(props.country)
  const [lang, setLang] = useState<Lang>(props.lang)
  const dictionary = useMemo(() => getDictionary(country, lang), [country, lang])
  const localeCode = useMemo(() => getLocaleCodeFromCountryLang(country, lang), [country, lang])
  const locale = useMemo(() => getLocaleFromCode(localeCode), [localeCode])
  const pathname = usePathname()

  // Keep the locale updated after RootLayout render
  useEffect(() => {
    const pathLocale = getLocaleFromPath(pathname)
    if (country !== pathLocale.countryCodeLowercase) {
      setCountry(pathLocale.countryCodeLowercase)
    }
    if (lang !== pathLocale.lang) {
      setLang(pathLocale.lang)
    }
  }, [pathname, country, lang])

  return (
    <LocaleContext.Provider
      value={{
        dictionary,
        locale,
      }}
    >
      {props.children}
    </LocaleContext.Provider>
  )
}

export const useLocale = (scope: keyof Dictionary = 'common', customLocale?: LocaleConfig) => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleContext')
  }

  // Use custom locale if provided, otherwise use context locale
  const locale = customLocale ?? context.locale

  // Get dictionary for the locale (custom or context)
  const dictionary = useMemo(() => {
    if (customLocale) {
      return getDictionary(customLocale.countryCodeLowercase, customLocale.lang)
    }
    return context.dictionary
  }, [customLocale, context.dictionary])

  const scopedDict = dictionary[scope]

  /** Replaces {{tokens}} in a string with object properties */
  function interpolate(
    str: string | undefined,
    obj: Record<string, string | JSX.Element | undefined>
  ) {
    return (str ?? '').replace(/\{\{([^}]+)\}\}/g, (_, token) => String(obj[token ?? '']))
  }

  /**
   * Translate a string
   * @param key - the key to translate
   * @param tokens - an object of tokens to replace in the string
   * @returns the translated string
   */
  function translate(key: string | undefined, tokens: Record<string, string | undefined> = {}) {
    if (!key) return ''
    return interpolate(scopedDict[key as keyof typeof scopedDict], tokens)
  }

  /**
   * Allow injecting React component into translated string with special tag.
   * Example: "By placing your order, you agree to the BODi <link1>Terms and Conditions<link1> and <link2>Privacy Policy<link2>."
   * @param translatedString - translated string
   * @param components - Map of functions to replace the tag in translated string
   * @returns the transformed string as React components
   */
  function transform(
    translatedString: string | undefined,
    components?: Record<string, (text: string) => JSX.Element>
  ) {
    if (!components) {
      return <>{translatedString}</>
    }
    return Object.entries(components).reduce<Array<string | JSX.Element | undefined>>(
      (acc, [key, valueFn]) => {
        return acc.flatMap((el, index) => {
          if (typeof el === 'string') {
            const [head, text, tail] = `${el}`.split(`<${key}>`)
            return [head, <span key={`${key}-${index}`}>{valueFn(text)}</span>, tail]
          }
          return el
        })
      },
      [translatedString]
    )
  }

  return {
    t: translate,
    transform,
    locale: locale,
  }
}

export type TranslateFn = ReturnType<typeof useLocale>['t']
